import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/api";
import { LangProvider } from "@/lib/i18n";
import { Toaster } from "@/components/ui/sonner";
import ThemeSwitcher, { initTheme } from "@/components/ThemeSwitcher";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import { ForgotPassword, ResetPassword } from "@/pages/AuthExtra";
import Catalog from "@/pages/Catalog";
import TrackDetail from "@/pages/TrackDetail";
import Lesson from "@/pages/Lesson";
import Exam from "@/pages/Exam";
import { CertificatePage, VerifyPage } from "@/pages/Certificate";
import { ApplyForm, Applications } from "@/pages/Apply";
import Landing from "@/pages/Landing";
import LiveClasses from "@/pages/LiveClasses";
import FinLab from "@/pages/FinLab";
import Career from "@/pages/Career";
import Employer from "@/pages/Employer";
import SuperAdmin from "@/pages/SuperAdmin";
import Builder from "@/pages/Builder";
import Students from "@/pages/Students";
import Assessor from "@/pages/Assessor";
import Admin from "@/pages/Admin";
import ForceChangePassword from "@/pages/ChangePassword";
import InstallBanner from "@/components/InstallBanner";
import Messages from "@/pages/Messages";
import VideoCall from "@/pages/VideoCall";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForceChangePassword />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return <Layout>{children}</Layout>;
}

function App() {
  initTheme();
  return (
    <div className="App">
      <LangProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/_archive/fscd-landing" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify/:certId" element={<VerifyPage />} />
              <Route path="/app" element={<Protected><Catalog /></Protected>} />
              <Route path="/messages" element={<Protected><Messages /></Protected>} />
              <Route path="/call/:room" element={<Protected><VideoCall /></Protected>} />
              <Route path="/live" element={<Protected><LiveClasses /></Protected>} />
              <Route path="/finlab" element={<Protected><FinLab /></Protected>} />
              <Route path="/career" element={<Protected><Career /></Protected>} />
              <Route path="/employer" element={<Protected roles={["employer", "admin", "superadmin"]}><Employer /></Protected>} />
              <Route path="/superadmin" element={<Protected roles={["superadmin", "admin"]}><SuperAdmin /></Protected>} />
              <Route path="/track/:id" element={<Protected><TrackDetail /></Protected>} />
              <Route path="/track/:id/lesson/:lid" element={<Protected><Lesson /></Protected>} />
              <Route path="/track/:id/exam" element={<Protected><Exam /></Protected>} />
              <Route path="/certificate/:certId" element={<Protected><CertificatePage /></Protected>} />
              <Route path="/apply/:trackId" element={<Protected><ApplyForm /></Protected>} />
              <Route path="/applications" element={<Protected><Applications /></Protected>} />
              <Route path="/assessor" element={<Protected roles={["assessor", "admin", "superadmin"]}><Assessor /></Protected>} />
              <Route path="/builder" element={<Protected roles={["assessor", "admin", "superadmin"]}><Builder /></Protected>} />
              <Route path="/students" element={<Protected roles={["assessor", "admin", "superadmin"]}><Students /></Protected>} />
              <Route path="/admin" element={<Protected roles={["admin", "superadmin"]}><Admin /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <InstallBanner />
          <Toaster position="top-center" richColors />
          <ThemeSwitcher />
        </AuthProvider>
      </LangProvider>
    </div>
  );
}

export default App;
